/**
 * Wire types for the AOTD emoji quiz. Mirror of the backend's
 * `n450s_backend/modules/aotd/quiz/types.ts`; the prose contract is
 * `n450s_backend/modules/aotd/quiz/CONTRACT.md`. Change both together.
 *
 * Times are ISO strings. Picks are flat, 4 per row (QUIZ_SLOTS_PER_ROW):
 * slots 0 to 2 of a row are its emojis, slot 3 its author's signature emoji.
 * Null is a blank or closed slot.
 *
 * Read by `artOfTheDayApi.getQuiz` / `postQuizGuess`; the socket messages are
 * applied to that cache in `quizSocket.ts`.
 */

import type { CardRarity } from "../../respectCards/types/cardTypes";

export const QUIZ_SLOTS_PER_ROW = 4;
export const QUIZ_WINDOW_MS = 45 * 60 * 1000;

export type QuizStatus = "open" | "closed";
export type QuizEndedBy = "perfect" | "timeout" | "admin";
export type QuizPick = string | null;

/** How one slot of a locked guess scored. Only sent after close. */
export type QuizMark = "exact" | "misplaced" | "wrong" | "blank" | "closed";

export interface QuizRowDto {
  userId: string;
  username: string;
  /** Emoji slots this author set (0 to 3); slots past it are closed. */
  emojiCount: number;
  /** False when the author has no signature emoji: the yellow slot is closed. */
  hasSignature: boolean;
  /**
   * Emojis this author burned on EARLIER AOTDs (today's excluded). They can
   * never be in today's answer, so the guess box refuses them in brick slots.
   */
  usedEmojis: string[];
}

export interface QuizGuessDto {
  /** Stable id of the guess (React key, dedupe against socket pushes). */
  id: string;
  username: string;
  isAuthor: boolean;
  /** The viewer's own guess. Always false on socket pushes; compare usernames. */
  isYou: boolean;
  /** 1, or 2 with Second Guess. */
  attempt: number;
  lockedAt: string;
  /** lockedAt minus the board's opensAt, in ms. */
  atMs: number;
  picks: QuizPick[];
  // after close only (absent while open)
  marks?: QuizMark[];
  score?: number;
  perfect?: boolean;
  /** 1-based rank among non-authors. Null for the author and for a superseded guess. */
  rank?: number | null;
  /** A player's weaker guess when they had two: shown faded, never ranked. */
  superseded?: boolean;
}

export interface QuizPrizeDto {
  instanceId: string;
  cardSlug: string;
  cardName: string;
  cardEmoji: string;
  rarity: CardRarity;
}

/**
 * The card the winner will get, drawn when the guessing opens (or set by an
 * admin). Shown while the quiz is open; `prize` takes over after the close.
 */
export interface QuizPrizePreviewDto {
  cardSlug: string;
  cardName: string;
  cardEmoji: string;
  rarity: CardRarity;
  description: string;
  /** The honest "when it acts" label the cards API sends. */
  effectLifetime: { kind: string; label: string } | null;
}

export interface QuizWinnerDto {
  userId: string;
  username: string;
  score: number;
  perfect: boolean;
  lockedAt: string;
  atMs: number;
}

export type QuizViewerReason =
  | "signed_out"
  | "not_a_member"
  | "closed"
  | "no_attempts_left";

export interface QuizViewerDto {
  signedIn: boolean;
  /** Any signed-in member (raced or not), or an author. */
  eligible: boolean;
  isAuthor: boolean;
  /** 1, or 2 after Second Guess. */
  attemptsAllowed: number;
  /** Locked, non-voided guesses so far. */
  attemptsUsed: number;
  /** open && eligible && attemptsUsed < attemptsAllowed. */
  canGuess: boolean;
  /** Why canGuess is false. Absent when it is true. */
  reason?: QuizViewerReason;
  /**
   * After a heist restart: the viewer's earlier picks, unlocked and remapped
   * onto the new board (their old row stays first). Null otherwise.
   */
  draft: QuizPick[] | null;
}

export interface QuizBoardDto {
  dayKey: string;
  aotdId: string;
  status: QuizStatus;
  /** Start of the current window (moves on a heist restart). */
  opensAt: string;
  closesAt: string;
  closedAt: string | null;
  endedBy: QuizEndedBy | null;
  rows: QuizRowDto[];
  /** Always 4. */
  slotsPerRow: number;
  /** Flat answer, 4 per row, null on closed slots. Null while open. */
  answer: QuizPick[] | null;
  /**
   * The live feed, oldest lock first. While open: picks and times, no
   * scores. After close: every field, with authors last.
   */
  guesses: QuizGuessDto[];
  /** After close. Null while open or when no non-author played. */
  winner: QuizWinnerDto | null;
  /** After close. The card the winner received (public). */
  prize: QuizPrizeDto | null;
  /** While open: the card the winner will get. Absent from older servers. */
  prizePreview?: QuizPrizePreviewDto | null;
  you: QuizViewerDto;
}

/** GET /art-of-the-day/quiz?dayKey= */
export interface GetQuizResponse {
  success: true;
  /** Null when today has no quiz (not published yet, or the author set no emojis). */
  quiz: QuizBoardDto | null;
  /** Server clock, for the burn-down bar. */
  serverNow: string;
}

/** POST /art-of-the-day/quiz/guess */
export interface PostQuizGuessBody {
  /** The board's dayKey; a guess for another day is refused. */
  dayKey: string;
  /** Flat, rows.length * 4 entries. */
  picks: QuizPick[];
}

export interface PostQuizGuessResponse {
  success: true;
  guess: QuizGuessDto;
  /** True when this guess was a non-author perfect and closed the quiz. */
  ended: boolean;
  /** The board after the guess, for this viewer. */
  quiz: QuizBoardDto;
}

export type QuizGuessErrorCode =
  | "auth_required"
  | "no_quiz"
  | "day_mismatch"
  | "quiz_closed"
  | "not_eligible"
  | "no_attempts_left"
  | "bad_picks"
  | "empty_guess"
  | "closed_slot"
  | "used_emoji"
  | "duplicate_pick"
  | "board_changed"
  | "rate_limited";

export interface QuizErrorResponse {
  success: false;
  code: QuizGuessErrorCode;
  message: string;
}

// race socket, the day channel

/** A guess as it goes to everyone. No scores, no `isYou`. */
export type QuizGuessPublicDto = Omit<
  QuizGuessDto,
  "isYou" | "marks" | "score" | "perfect" | "rank" | "superseded"
>;

export interface QuizOpenedSocketMessage {
  type: "quiz.opened";
  dayKey: string;
  opensAt: string;
  closesAt: string;
  /** True when a heist re-publish restarted the quiz. */
  restarted: boolean;
  ts?: string;
}

export interface QuizGuessSocketMessage {
  type: "quiz.guess";
  dayKey: string;
  guess: QuizGuessPublicDto;
  ts?: string;
}

export interface QuizClosedSocketMessage {
  type: "quiz.closed";
  dayKey: string;
  endedBy: QuizEndedBy;
  closedAt: string;
  /** Null when nobody but the author played. Refetch the board for the rest. */
  winnerUsername: string | null;
  ts?: string;
}

export type QuizSocketMessage =
  | QuizOpenedSocketMessage
  | QuizGuessSocketMessage
  | QuizClosedSocketMessage;

/** Route paths relative to `API_BASE_URL`, in the style of `aotdPaths`. */
export const quizPaths = {
  board: "/art-of-the-day/quiz",
  guess: "/art-of-the-day/quiz/guess",
  prize: "/art-of-the-day/quiz/prize",
} as const;

/** PUT /art-of-the-day/quiz/prize (admin): the card the winner will get. */
export interface PutQuizPrizeBody {
  dayKey: string;
  cardSlug: string;
}
